import React, { forwardRef, Suspense, useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_ADMIN_ROUTE, SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE } from "../../../../../../constants/routes"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useNavigate } from "react-router"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    editTermsAndCondition,
    fetchTermsAndConditionsById,
    FetchTermsAndConditionsResponse,
    TermsAndConditionQuery,
} from "../termsAndConditionApi"
import { LanguageCode, TermsAndConditionsFormValues } from "../termsAndConditionTypes"
import { CreateOrEditTermsAndConditionForm } from "../components/CreateEditTermsAndConditionForm"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"

function transformData(data: FetchTermsAndConditionsResponse) {
    return data.termsAndConditionsDetails
}

export function EditTermsAndConditions() {
    const formRef = useRef<FormikProps<TermsAndConditionsFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-settings-terms-and-conditions" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_TERMS_AND_CONDITIONS_UPDATE}
                    title="Terms and conditions"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="supplier-admin-settings-terms-and-conditions-update-button">
                            <StandardButton onClick={handleSaveClick}>Update</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-settings">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-terms-and-conditions">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE}>
                                Terms and conditions
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-update-terms-and-conditions">
                            <Breadcrumb>Update terms and conditions</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <TermsAndCondition ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const TermsAndCondition = forwardRef<FormikProps<TermsAndConditionsFormValues>>((_, ref) => {
    const navigate = useNavigate()

    const { termsAndConditionsId } = useParams()
    const termsAndConditionsIdNumber = Number(termsAndConditionsId)

    const { data } = useApiQuery(fetchTermsAndConditionsById, {
        queryName: TermsAndConditionQuery.DETAILS,
        dependencies: {
            termsAndConditionsId: termsAndConditionsIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(termsAndConditionsId),
        select: transformData,
    })

    const { mutate: callEditTermsAndConditions } = useApiMutation(editTermsAndCondition, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TermsAndConditionQuery.LIST] })
            navigate(SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE)
        },
    })

    const submitForm = (value: TermsAndConditionsFormValues) => {
        if (!termsAndConditionsId) throw new Error("Terms And Conditions ID is undefined")
        if (!value.type) throw new Error("type is required")

        const termsAndConditions = {
            callName: value.callName,
            languageCode: value.languageCode,
            text: value.text,
            type: value.type,
            isActive: value.isActive,
        }

        callEditTermsAndConditions({
            termsAndConditionsId,
            termsAndConditions,
        })
    }

    const initialValues: TermsAndConditionsFormValues = {
        callName: data?.callName ?? "",
        languageCode: data?.languageCode ?? LanguageCode.DA,
        type: data?.type ?? "",
        text: data?.text ?? "",
        isActive: data?.isActive ?? true,
    }

    return (
        <CreateOrEditTermsAndConditionForm
            ref={ref}
            initialValues={initialValues}
            history={data?.history}
            version={data?.version}
            onSubmit={submitForm}
        />
    )
})
